import Button from 'components/common/buttons/Button';
import { Colors, Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ActionsList from 'components/common/ActionsList';
import { getFontFaceFromStyle } from 'support/textUtils';
import EmailInputField from '../common/EmailInputField';
import TextLabel from 'components/common/TextLabel';
export interface ForgotPasswordContentTemplateSpec
  extends Lightning.Component.TemplateSpec {
  email: string;
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  UserInputs: typeof ActionsList;
  ActionsList: typeof ActionsList;
}

const CONTENT_OFFSET_Y = 376;
const WIDTH = 684;
const SPACING = 16;

const TITLE_FONT_SIZE = 36;
const TITLE_HEIGHT = 58;

const EMAIL_DESC_FONT_SIZE = 22;
const EMAIL_DESC_LINE_HEIGHT = 35;
const EMAIL_MARGIN_TOP = 24;

const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

const EMAIL_REF = 'EmailInput';

export default class ForgotPasswordContent extends HoverableComponent<ForgotPasswordContentTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _Description = this.getByRef('Description')!;
  private _ActionsList = this.getByRef('ActionsList')!;
  private _EmailInput = this._ActionsList.getItemByRef(
    EMAIL_REF,
  ) as EmailInputField;

  set email(value: string) {
    this._EmailInput.input = value;
  }

  get announce() {
    return [this._Title.text!.text, this._Description.text!.text];
  }

  static override _template(): Lightning.Component.Template<ForgotPasswordContentTemplateSpec> {
    return {
      y: CONTENT_OFFSET_Y,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Title: {
        type: TextLabel,
        h: TITLE_HEIGHT,
        text: {
          text: translate('account.forgotPassword.title'),
          textAlign: 'center',
          fontSize: TITLE_FONT_SIZE,
          fontFace: getFontFaceFromStyle('bold'),
          textColor: Colors('labelText').get(),
        },
      },
      Description: {
        type: TextLabel,
        w: WIDTH,
        text: {
          text: translate('account.forgotPassword.description'),
          textAlign: 'center',
          fontSize: EMAIL_DESC_FONT_SIZE,
          lineHeight: EMAIL_DESC_LINE_HEIGHT,
        },
      },
      ActionsList: {
        flex: {
          direction: 'column',
        },
        type: ActionsList,
        spacing: SPACING,
        signals: {
          $onHover: true,
          $onEmailSubmit: '$onEmailSubmit',
        },
        passSignals: {
          $openKeyboard: true,
          $onEmailSubmit: true,
        },
        items: [
          {
            ref: EMAIL_REF,
            type: EmailInputField,
            flexItem: {
              marginTop: EMAIL_MARGIN_TOP,
            },
            signals: { $onHover: '$onHover' },
            passSignals: {
              $onHover: '$onHover',
              $emailInputSelected: '$openKeyboard',
            },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
          },
          {
            type: Button,
            minWidth: WIDTH,
            height: BUTTON_HEIGHT,
            fontSize: BUTTON_FONT_SIZE,
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            label: translate('account.forgotPassword.sendResetLink'),
            action: '$onEmailSubmit',
            signals: { $onHover: '$onHover' },
            passSignals: { $onEmailSubmit: true },
          },
        ],
      },
    };
  }

  override _onActive() {
    this._setState('ActionsList');
  }

  private $onEmailSubmit() {
    this._EmailInput.checkValid().then(isValid => {
      if (isValid) this.signal('$onEmailSubmit', this._EmailInput.input);
    });
  }

  private $onHover(target: any) {
    if (target.id === this._ActionsList.id) {
      this._setState('ActionsList');
      this.signal('$onContentHovered');
    }
  }

  static override _states() {
    return [
      class ActionsList extends this {
        override _getFocused() {
          return this._ActionsList;
        }
      },
    ];
  }
}
